import * as React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Field, FieldContent, FieldError, FieldLabel } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader, Plus } from "lucide-react";
const ChainRequest = React.lazy(() =>
  import("./ChainRequest").then((module) => ({ default: module.ChainRequest }))
);
const CreateRequestCategoryDialog = React.lazy(() => import("./CreateRequestCategoryDialog"));

import { useRequestCategories } from "@/hooks/useRequestCategories";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGroups } from "@/hooks/useGroups";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export const RequestForm = ({
  isGeneratingTitle,
  type,
}: {
  isGeneratingTitle?: boolean;
  type: "discuss" | "research";
}) => {
  const { control, watch, setValue } = useFormContext();
  const { data: categories } = useRequestCategories();
  const selectedGroups = watch("groups", []);
  const { data: groupsData, isLoading } = useGroups();
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = React.useState(false);
  const [searchGroup, setSearchGroup] = React.useState("");

  const toggleGroup = (groupId: string) => {
    const currentGroups = selectedGroups || [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter((id: string) => id !== groupId)
      : [...currentGroups, groupId];
    setValue("groups", newGroups, { shouldDirty: true });
  };

  return (
    <div className="flex flex-col gap-4">
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className="gap-1">
            <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
              Request Name
            </FieldLabel>
            <FieldContent>
              <div className="relative">
                <Input
                  {...field}
                  placeholder={isGeneratingTitle ? "" : "Step by Step Guide"}
                  className="h-8 text-xs pr-8"
                  disabled={isGeneratingTitle}
                />
                {isGeneratingTitle && (
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                    <span className="text-xs text-neutral-500">Generating title</span>
                    <span className="flex space-x-0.5 pt-1">
                      <span className="w-0.5 h-0.5 bg-neutral-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                      <span className="w-0.5 h-0.5 bg-neutral-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                      <span className="w-0.5 h-0.5 bg-neutral-500 rounded-full animate-bounce"></span>
                    </span>
                  </div>
                )}
                {isGeneratingTitle && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader className="h-4 w-4 animate-spin text-neutral-400" />
                  </div>
                )}
              </div>
              <FieldError errors={[fieldState.error]} />
            </FieldContent>
          </Field>
        )}
      />
      <Controller
        name="content"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className="gap-1">
            <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
              Request Content
            </FieldLabel>
            <FieldContent>
              <Textarea
                {...field}
                placeholder="Lorem ipsum dolor sit amet, consectetur adipiscing elit."
                className="text-xs min-h-[60px]"
              />
              <FieldError errors={[fieldState.error]} />
            </FieldContent>
          </Field>
        )}
      />
      <Controller
        name="category_id"
        control={control}
        render={({ field, fieldState }) => (
          <Field data-invalid={fieldState.invalid} className="gap-1">
            <div className="flex items-center justify-between">
              <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
                Category
              </FieldLabel>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      className="size-6"
                      size={"icon-sm"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsCreateCategoryOpen(true);
                      }}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-[10px]">Create new category</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <FieldContent>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full px-2 text-xs font-medium h-8">
                  <SelectValue placeholder="Select a Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    ?.filter((cat) => !cat.parent_id)
                    .map((category) => (
                      <SelectItem key={category._id} value={category._id} className="text-xs">
                        {category.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </FieldContent>
            <FieldError errors={[fieldState.error]} />
          </Field>
        )}
      />
      <React.Suspense fallback={<Loader className="animate-spin" />}>
        <ChainRequest type={type} />
        <CreateRequestCategoryDialog
          open={isCreateCategoryOpen}
          onOpenChange={setIsCreateCategoryOpen}
        />
      </React.Suspense>
      <div className="space-y-2">
        <FieldLabel className="font-medium text-xs leading-4 text-neutral-400">
          Search Group to share
        </FieldLabel>
        <Input
          placeholder="Enter group name"
          className="h-8 text-xs"
          value={searchGroup}
          onChange={(e) => setSearchGroup(e.target.value)}
        />
        <ScrollArea className="border rounded-md h-24">
          <div className="p-2 flex flex-wrap gap-2">
            {isLoading ? (
              <p className="text-sm text-neutral-400">Loading groups...</p>
            ) : (
              groupsData?.data
                .filter((group) => group.name.toLowerCase().includes(searchGroup.toLowerCase()))
                .map((group) => {
                  const isSelected = selectedGroups.includes(group.id);
                  return (
                    <Badge
                      key={group.id}
                      variant="outline"
                      onClick={() => toggleGroup(group.id)}
                      className={cn(
                        "rounded-md text-neutral-500 cursor-pointer",
                        isSelected && "bg-primary-300 text-neutral-white border-primary-300"
                      )}
                    >
                      {isSelected && <Check />}
                      {group.name}
                    </Badge>
                  );
                })
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};
